import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { Topic } from '../../../types/topic';
import { ROUTES } from '../../../router/routes';
import { Icon } from '@iconify/react';
import { useDirection } from '../../../contexts/DirectionContext';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDate } from '../../../utils/formatters';
import { useTranslation } from 'react-i18next';

interface TopicsGroupProps {
  letter: string;
  topics: Topic[];
}

const ICONS = {
  caretUp: 'ph:caret-up-thin',
  caretDown: 'ph:caret-down-thin',
  playCircle: 'ph:play-circle-thin',
  clock: 'ph:clock-thin',
  arrowLeft: 'ph:arrow-left-thin',
  arrowRight: 'ph:arrow-right-thin'
} as const;

// Subcomponents
const TopicButton: React.FC<{
  topic: Topic;
  isExpanded: boolean;
  onClick: () => void;
  isDark: boolean;
}> = ({ topic, isExpanded, onClick, isDark }) => (
  <motion.button
    layout="position"
    initial={false}
    onClick={onClick}
    className={`
      w-full sm:w-auto sm:min-w-[280px] sm:max-w-[400px] sm:flex-1
      px-4 py-3
      flex items-center gap-4
      text-start
      transition-colors duration-200
      border-0 sm:border sm:border-transparent sm:rounded-lg
      ${isDark
        ? 'hover:bg-surface-secondary sm:hover:border-border-primary'
        : 'hover:bg-surface-primary sm:hover:border-border-primary'}
      ${isExpanded 
        ? 'bg-surface-secondary sm:border-border-primary' 
        : (isDark ? 'border-b border-border-secondary' : 'border-b border-border-primary/5 sm:border-b-0')}
    `}
  >
    <div className="flex-1 min-w-0">
      <h3 className={`
        font-semibold text-lg tracking-tight
        transition-colors duration-200
        ${isDark ? 'text-text-primary' : 'text-text-primary'}
      `}>
        {topic.title}
      </h3>
      <p className={`
        text-sm truncate mt-1
        ${isDark ? 'text-text-secondary opacity-70' : 'text-text-primary opacity-70'}
      `}>
        {topic.description}
      </p>
    </div>
    
    <div className="flex items-center gap-3">
      <span className={`
        shrink-0 px-3 py-0.5 rounded-full text-xs
        ${isDark
          ? 'bg-surface-primary/20 text-text-secondary opacity-70'
          : 'bg-surface-primary/30 text-text-secondary opacity-70'}
      `}>
        {topic.count}
      </span>
      <Icon
        icon={isExpanded ? ICONS.caretUp : ICONS.caretDown}
        className="w-5 h-5 text-text-secondary"
      />
    </div>
  </motion.button>
);

const MobileExpandedView: React.FC<{
  topic: Topic;
  isDark: boolean;
  onBrowse: () => void;
  isRTL: boolean;
  t: (key: string) => string;
  language: string;
}> = ({ topic, isDark, onBrowse, isRTL, t, language }) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  return (
    <div className="sm:hidden flex flex-col">
      {/* Thumbnail Banner */}
      {topic.thumbnail && (
        <div className="relative w-full aspect-[2/1]">
          {/* Background Gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          
          {/* Image */}
          <img 
            src={topic.thumbnail} 
            alt={topic.title}
            className="w-full h-full object-cover"
          />

          {/* Video Count Label */}
          <div className="absolute bottom-3 left-3 right-3 flex items-center">
            <div className="px-2 py-1 rounded bg-black/40 backdrop-blur-[2px]">
              <span className="text-xs font-medium text-white/90">
                {topic.count} {t('topics.stats.videos')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="flex flex-col p-4 pt-3">
        {/* Description */}
        {topic.description && topic.description.trim() && (
          <div className="relative mb-4">
            <div 
              onClick={() => !isDescriptionExpanded && setIsDescriptionExpanded(true)}
              className={`
                text-sm leading-relaxed
                ${!isDescriptionExpanded ? 'cursor-pointer' : ''}
                ${isDark ? 'text-text-secondary' : 'text-text-secondary'}
              `}
            >
              <p className={!isDescriptionExpanded ? 'line-clamp-3' : ''}>
                {topic.description}
              </p>
              {isDescriptionExpanded && topic.description.length > 100 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDescriptionExpanded(false);
                  }}
                  className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-primary-500"
                >
                  {t('common.seeLess')}
                  <Icon 
                    icon={ICONS.caretUp}
                    className="w-3.5 h-3.5" 
                  />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={onBrowse}
          className={`
            flex items-center justify-center gap-2
            w-full py-2.5 px-4 rounded-full
            font-medium text-sm
            transition-all duration-200
            ${isDark
              ? 'bg-primary-500/10 hover:bg-primary-500/20 text-primary-300'
              : 'bg-primary-500/10 hover:bg-primary-500/20 text-primary-500'}
          `}
        >
          {t('topics.actions.browseVideos')}
          <Icon 
            icon={isRTL ? ICONS.arrowLeft : ICONS.arrowRight}
            className="w-4 h-4" 
          />
        </button>
      </div>
    </div>
  );
};

const DesktopExpandedView: React.FC<{
  topic: Topic;
  isDark: boolean;
  onBrowse: () => void;
  isRTL: boolean;
  t: (key: string) => string;
  language: string;
}> = ({ topic, isDark, onBrowse, isRTL, t, language }) => (
  <div className="hidden sm:flex flex-col gap-4 p-4 sm:p-0">
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <span className={`${isDark ? 'text-text-secondary opacity-70' : 'text-text-secondary'}`}>{t('topics.stats.videos')}:</span>
        <span className="text-text-primary">{topic.count}</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className={`${isDark ? 'text-text-secondary opacity-70' : 'text-text-secondary'}`}>{t('topics.stats.lastUpdated')}:</span>
        <span className="text-text-primary">
          {formatDate(topic.updatedAt, language)}
        </span>
      </div>
    </div>
    <button
      onClick={onBrowse}
      className={`
        flex items-center justify-center gap-2
        w-full py-2.5 px-4 rounded-full
        font-medium text-base
        transition-all duration-200
        ${isDark
          ? 'bg-[rgba(var(--colors-primary-500-rgb),0.1)] hover:bg-[rgba(var(--colors-primary-500-rgb),0.2)] text-primary-300'
          : 'bg-[rgba(var(--colors-primary-500-rgb),0.1)] hover:bg-[rgba(var(--colors-primary-500-rgb),0.2)] text-primary-500'}
      `}
    >
      {t('topics.actions.browseVideos')}
      <Icon 
        icon={isRTL ? ICONS.arrowLeft : ICONS.arrowRight}
        className="w-5 h-5" 
      />
    </button>
  </div>
);

const TopicsGroup: React.FC<TopicsGroupProps> = ({ letter, topics }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { isRTL } = useDirection();
  const [expandedTopic, setExpandedTopic] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const isDark = theme.mode === 'dark';

  const handleTopicClick = (topicId: string) => {
    const searchParams = new URLSearchParams();
    searchParams.set("topics", topicId);
    searchParams.set("contentType", "video");
    navigate(`${ROUTES.SEARCH}?${searchParams.toString()}`);
  };

  return (
    <div className="relative">
      {/* Letter Header */}
      <div className={`
        sticky [top:var(--header-total-height)] z-10
        flex items-center
        py-6 px-0 sm:px-8 mb-0 sm:mb-6
        backdrop-blur-md
        ${isDark 
          ? 'bg-background-primary/80 border-b border-primary-500' 
          : 'bg-background-primary/90 border-b border-primary-500'}
      `}>
        <h2 className={`
          text-3xl sm:text-4xl font-major font-bold tracking-tight px-4 sm:px-0
          ${isDark ? 'text-primary-400' : 'text-primary-600'}
        `}>
          {letter}
        </h2>
      </div>

      {/* Topics List */}
      <div className="px-0 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:gap-3">
          {topics.map((topic) => (
            <React.Fragment key={topic.id}>
              <TopicButton
                topic={topic}
                isExpanded={expandedTopic === topic.id}
                onClick={() => setExpandedTopic(expandedTopic === topic.id ? null : topic.id)}
                isDark={isDark}
              />

              <AnimatePresence>
                {expandedTopic === topic.id && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                    className={`
                      w-full overflow-hidden
                      ${isDark
                        ? 'bg-surface-secondary/30 border-y border-border-secondary'
                        : 'bg-surface-primary/30 border-y border-border-primary/5'}
                    `}
                  >
                    <div className={`
                      p-4 sm:p-6 grid gap-6
                      grid-cols-1 sm:grid-cols-[1fr_300px]
                      ${topic.thumbnail ? 'md:grid-cols-[120px_1fr_300px]' : ''}
                      ${isRTL ? 'direction-rtl' : 'direction-ltr'}
                    `}>
                      {/* Desktop Thumbnail */}
                      {topic.thumbnail && (
                        <div className="hidden md:block w-[120px] aspect-[4/3] rounded-lg overflow-hidden">
                          <img 
                            src={topic.thumbnail} 
                            alt={topic.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}

                      {/* Main Content */}
                      <div className="hidden sm:block space-y-3">
                        <h4 className={`
                          text-xl font-semibold tracking-tight
                          ${isDark ? 'text-text-primary' : 'text-text-primary'}
                        `}>
                          {topic.title}
                        </h4>
                        {topic.description && topic.description.trim() && (
                          <p className={`
                            text-base leading-relaxed
                            ${isDark ? 'text-text-secondary opacity-70' : 'text-text-secondary'}
                          `}>
                            {topic.description}
                          </p>
                        )}
                      </div>

                      {/* Stats & Action */}
                      <div className={`
                        flex flex-col col-span-1 sm:col-span-1
                        ${isDark
                          ? 'bg-surface-secondary border border-border-secondary'
                          : 'bg-background-secondary border border-border-primary/5'}
                        rounded-lg overflow-hidden
                        sm:p-4 sm:gap-4
                      `}>
                        <MobileExpandedView
                          topic={topic}
                          isDark={isDark}
                          onBrowse={() => handleTopicClick(topic.id)}
                          isRTL={isRTL}
                          t={t}
                          language={i18n.language}
                        />
                        <DesktopExpandedView
                          topic={topic}
                          isDark={isDark}
                          onBrowse={() => handleTopicClick(topic.id)}
                          isRTL={isRTL}
                          t={t}
                          language={i18n.language}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TopicsGroup; 